import React, { useEffect, useState } from 'react';
import Navbar from "../components/layout/Navbar";
import HeroBit from "../components/bits/HeroBit";
import BrandStoryBit from "../components/bits/BrandStoryBit";
import EditorialBit from "../components/bits/EditorialBit";
import PhilosophyBit from "../components/bits/PhilosophyBit";
import NewsletterBit from "../components/bits/NewsletterBit";
import Footer from "../components/layout/Footer";
import ProductGridBit from "../components/shop/ProductGridBit";
import LuxuryLoading from '../components/ui/LuxuryLoading';
import { ProductService } from "../api/product.api";
import { CartService } from "../api/cart.api";
import { useNavigate } from "react-router-dom";
import BrandOwnerBit from "../components/bits/BrandOwnerBit";

const HomePage = () => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cartLoading, setCartLoading] = useState({});
    const [categories, setCategories] = useState([]);
    const [concerns, setConcerns] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);

        // Simulate initial page load
        const timer = setTimeout(() => {
            fetchInitialData();
        }, 800);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch products (all products instead of just featured)
            const productsData = await ProductService.getProducts({
                limit: 12, // Show 12 products on homepage
                sort: 'popular' // Sort by popular/featured
            });

            // Handle the response format
            const products = Array.isArray(productsData)
                ? productsData
                : (productsData.data || productsData.products || []);

            setProducts(products);

            // Fetch categories for filtering (optional)
            try {
                const categoriesData = await ProductService.getCategories();
                setCategories(categoriesData || []);
            } catch (categoryError) {
                console.warn('Could not fetch categories:', categoryError);
                setCategories(getMockCategories());
            }

            // Extract concerns from products
            if (products.length > 0) {
                const allConcerns = products.reduce((acc, product) => {
                    if (product.concerns && Array.isArray(product.concerns)) {
                        product.concerns.forEach(concern => {
                            if (concern && !acc.includes(concern)) {
                                acc.push(concern);
                            }
                        });
                    }
                    return acc;
                }, []);

                setConcerns(allConcerns.map(concern => ({
                    id: concern.toLowerCase().replace(/\s+/g, '-'),
                    label: concern
                })).slice(0, 10)); // Limit to top 10 concerns
            }

            setPageLoaded(true);

        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to load products');
            // Set mock data for demo/fallback
            setProducts(getMockProducts());
            setCategories(getMockCategories());
            setConcerns(getMockConcerns());
            setPageLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    // Mock data for demo/fallback
    const getMockProducts = () => {
        return [
            {
                _id: '1',
                id: '1',
                name: 'Luminous Repair Elixir',
                category: 'serum',
                description: 'A transformative vitamin C serum that illuminates while repairing.',
                price: 185,
                originalPrice: 220,
                discount: 16,
                discountPrice: 155,
                rating: 4.9,
                reviewCount: 24,
                images: ['https://images.unsplash.com/photo-1556228453-efd6c1ff04f6'],
                variants: [
                    { id: '30ml', name: '30ml', price: 185 },
                    { id: '50ml', name: '50ml', price: 280 }
                ],
                isFeatured: true,
                isNew: true,
                isBestseller: false,
                concerns: ['Dullness', 'Uneven Texture'],
                ingredients: ['15% Stable Vitamin C', 'Camellia Japonica Extract'],
                freeFrom: ['Parabens', 'Sulfates'],
                createdAt: new Date().toISOString()
            },
            {
                _id: '2',
                id: '2',
                name: 'Hydra-Fix Concentrate',
                category: 'serum',
                description: 'Intense hydration with hyaluronic acid complex.',
                price: 165,
                originalPrice: 200,
                discount: 18,
                discountPrice: 135,
                rating: 4.7,
                reviewCount: 18,
                images: ['https://images.unsplash.com/photo-1596462502278-27bfdc403348'],
                variants: [
                    { id: '30ml', name: '30ml', price: 165 },
                    { id: '50ml', name: '50ml', price: 250 }
                ],
                isFeatured: true,
                isBestseller: true,
                concerns: ['Dehydration', 'Dryness'],
                createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
            },
            {
                _id: '3',
                id: '3',
                name: 'Night Renewal Elixir',
                category: 'treatment',
                description: 'Overnight repair and rejuvenation.',
                price: 195,
                originalPrice: 230,
                discount: 15,
                discountPrice: 165,
                rating: 4.8,
                reviewCount: 32,
                images: ['https://images.unsplash.com/photo-1541643600914-78b084683601'],
                variants: [
                    { id: '30ml', name: '30ml', price: 195 },
                    { id: '50ml', name: '50ml', price: 295 }
                ],
                isFeatured: true,
                concerns: ['Fine Lines', 'Aging'],
                createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
            },
            {
                _id: '4',
                id: '4',
                name: 'Radiance Boosting Cream',
                category: 'moisturizer',
                description: 'Daily moisturizer with vitamin C.',
                price: 145,
                originalPrice: 180,
                discount: 19,
                discountPrice: 120,
                rating: 4.6,
                reviewCount: 21,
                images: ['https://images.unsplash.com/photo-1556228578-9c360e1d8f1b'],
                variants: [
                    { id: '50ml', name: '50ml', price: 145 },
                    { id: '100ml', name: '100ml', price: 240 }
                ],
                isFeatured: true,
                isNew: true,
                concerns: ['Dullness', 'Dryness'],
                createdAt: new Date().toISOString()
            },
            {
                _id: '5',
                id: '5',
                name: 'Purifying Clay Mask',
                category: 'mask',
                description: 'Deep cleansing mask for clear skin.',
                price: 85,
                originalPrice: 100,
                discount: 15,
                discountPrice: 72,
                rating: 4.5,
                reviewCount: 15,
                images: ['https://images.unsplash.com/photo-1591085686350-798c0f9faa7f'],
                isBestseller: true,
                concerns: ['Acne', 'Oiliness'],
                createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
            },
            {
                _id: '6',
                id: '6',
                name: 'Soothe & Calm Toner',
                category: 'toner',
                description: 'Gentle toner for sensitive skin.',
                price: 75,
                rating: 4.4,
                reviewCount: 12,
                images: ['https://images.unsplash.com/photo-1556228579-35c4d4fa931f'],
                concerns: ['Sensitivity', 'Redness'],
                createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
            }
        ];
    };

    const getMockCategories = () => {
        return [
            { _id: 'serum', name: 'Serums', count: 3 },
            { _id: 'moisturizer', name: 'Moisturizers', count: 1 },
            { _id: 'treatment', name: 'Treatments', count: 1 },
            { _id: 'mask', name: 'Masks', count: 1 },
            { _id: 'toner', name: 'Toners', count: 1 }
        ];
    };

    const getMockConcerns = () => {
        return [
            { id: 'dullness', label: 'Dullness' },
            { id: 'uneven-texture', label: 'Uneven Texture' },
            { id: 'dehydration', label: 'Dehydration' },
            { id: 'dryness', label: 'Dryness' },
            { id: 'fine-lines', label: 'Fine Lines' },
            { id: 'aging', label: 'Aging' },
            { id: 'acne', label: 'Acne' },
            { id: 'oiliness', label: 'Oiliness' },
            { id: 'sensitivity', label: 'Sensitivity' },
            { id: 'redness', label: 'Redness' }
        ];
    };

    const handleAddToCart = async (productId) => {
        try {
            setCartLoading(prev => ({ ...prev, [productId]: true }));
            await CartService.addToCart({
                productId,
                quantity: 1
            });
            navigate('/cart');
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            setCartLoading(prev => ({ ...prev, [productId]: false }));
        }
    };

    const handleViewProduct = (product) => {
        const productSlug = product.slug || product.id || product._id;
        navigate(`/product/${productSlug}`);
    };

    // Helper function to get featured products from all products (for optional filtering)
    const getFeaturedProducts = () => {
        return products.filter(product =>
            product.isFeatured !== false
        ).slice(0, 8); // Limit to 8 featured products
    };

    // Helper function to get new arrivals
    const getNewArrivals = () => {
        return products.filter(product =>
            product.isNew ||
            (product.createdAt &&
                new Date(product.createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        ).slice(0, 4);
    };

    // Helper function to get bestsellers
    const getBestsellers = () => {
        return products.filter(product =>
            product.isBestseller ||
            product.rating >= 4.8
        ).slice(0, 4);
    };

    // Show full page loader while data is being fetched
    if (loading && !pageLoaded) {
        return (
            <div className="min-h-screen bg-pearl flex flex-col items-center justify-center">
                <div className="text-center">
                    <LuxuryLoading size="large" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-pearl overflow-x-hidden">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero Section */}
            <section id="hero" className="relative w-full">
                <HeroBit />
            </section>

            {/* Brand Story */}
            {/*<section id="story" className="section-spacing px-4 sm:px-6 lg:px-8">*/}
            {/*    <div className="container-luxury">*/}
            {/*        <BrandStoryBit />*/}
            {/*    </div>*/}
            {/*</section>*/}

            {/* Featured Products Section */}
            <section id="products" className="section-spacing bg-nude/30 px-4 sm:px-6 lg:px-8">
                <div className="container-luxury">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-heading font-light text-charcoal mb-4">
                            Featured <span className="text-champagne italic">Collections</span>
                        </h2>
                        <p className="text-lg text-charcoal/60 max-w-2xl mx-auto">
                            Discover our most loved products, carefully crafted for transformative results
                        </p>
                    </div>

                    {error ? (
                        <div className="text-center py-12">
                            <p className="text-charcoal/60 mb-4">{error}</p>
                            <button
                                onClick={fetchInitialData}
                                className="px-6 py-2 bg-champagne text-white rounded-full hover:bg-champagne/90 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-charcoal/60 mb-4">No products found</p>
                            <button
                                onClick={fetchInitialData}
                                className="px-6 py-2 bg-champagne text-white rounded-full hover:bg-champagne/90 transition-colors"
                            >
                                Refresh Products
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Using ProductGridBit component with all products */}
                            <ProductGridBit
                                products={products}
                                filteredProducts={products} // Show all products initially
                                isFiltering={false}
                                categories={categories}
                                concerns={concerns}
                                onAddToCart={handleAddToCart}
                                onViewProduct={handleViewProduct}
                                cartLoading={cartLoading}
                                showFilters={false} // Don't show filters on homepage
                            />

                            {/* Stats Section */}
                            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                                <div className="p-6 bg-white/50 rounded-2xl backdrop-blur-sm">
                                    <div className="text-3xl font-heading text-champagne mb-2">
                                        {products.length}
                                    </div>
                                    <div className="text-sm text-charcoal/60 uppercase tracking-wider">
                                        Total Products
                                    </div>
                                </div>
                                <div className="p-6 bg-white/50 rounded-2xl backdrop-blur-sm">
                                    <div className="text-3xl font-heading text-champagne mb-2">
                                        {getNewArrivals().length}
                                    </div>
                                    <div className="text-sm text-charcoal/60 uppercase tracking-wider">
                                        New Arrivals
                                    </div>
                                </div>
                                <div className="p-6 bg-white/50 rounded-2xl backdrop-blur-sm">
                                    <div className="text-3xl font-heading text-champagne mb-2">
                                        {getFeaturedProducts().length}
                                    </div>
                                    <div className="text-sm text-charcoal/60 uppercase tracking-wider">
                                        Featured Products
                                    </div>
                                </div>
                                <div className="p-6 bg-white/50 rounded-2xl backdrop-blur-sm">
                                    <div className="text-3xl font-heading text-champagne mb-2">
                                        {getBestsellers().length}
                                    </div>
                                    <div className="text-sm text-charcoal/60 uppercase tracking-wider">
                                        Best Sellers
                                    </div>
                                </div>
                            </div>

                            {/* View All Button */}
                            <div className="text-center mt-12">
                                <a
                                    href="/shop"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-charcoal border border-nude rounded-full hover:border-champagne hover:text-champagne transition-all duration-300"
                                >
                                    <span>Shop All Products</span>
                                    <span>→</span>
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* Editorial */}
            <section id="editorial" className="section-spacing overflow-hidden px-4 sm:px-6 lg:px-8">
                <div className="container-luxury">
                    <EditorialBit />
                </div>
            </section>

            {/* Philosophy */}
            {/*<section id="philosophy" className="section-spacing px-4 sm:px-6 lg:px-8">*/}
            {/*    <div className="container-luxury">*/}
            {/*        <PhilosophyBit />*/}
            {/*    </div>*/}
            {/*</section>*/}

            <section id="founders" className="section-spacing px-4 sm:px-6 lg:px-8">
                <div className="container-luxury">
                    <BrandOwnerBit />
                </div>
            </section>
            {/* Newsletter */}
            <section id="newsletter" className="section-spacing bg-charcoal px-4 sm:px-6 lg:px-8">
                <div className="container-luxury">
                    <NewsletterBit />
                </div>
            </section>


            {/* Footer */}
            <Footer />
        </div>
    );
};

export default HomePage;